import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "border text-gray-900 bg-white shadow flex flex-col relative" }
const _hoisted_2 = { class: "flex-1 flex flex-col gap-8 overflow-auto px-4 pt-8 pb-3" }
const _hoisted_3 = { class: "flex-1 flex flex-col gap-8" }
const _hoisted_4 = { class: "flex gap-2 items-center justify-between w-full" }
const _hoisted_5 = { class: "flex-1 truncate" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "text-sm flex items-center font-normal gap-1 text-gray-900 hover:underline cursor-pointer" }
const _hoisted_8 = {
  key: 0,
  class: "flex items-center my-1"
}
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { class: "border-t border-gray-300 grow" }, null, -1)
const _hoisted_10 = { class: "px-3 text-gray-500 text-sm" }
const _hoisted_11 = { class: "flex items-center gap-1" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("div", { class: "border-t border-gray-300 grow" }, null, -1)
const _hoisted_13 = ["onDblclick", "onDragstart", "onDragend"]
const _hoisted_14 = {
  key: 0,
  class: "flex justify-center"
}
const _hoisted_15 = {
  key: 0,
  class: "font-normal"
}
const _hoisted_16 = {
  key: 0,
  class: "text-gray-600 text-sm absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center"
}
const _hoisted_17 = /*#__PURE__*/_createElementVNode("div", { class: "flex flex-col gap-2 h-full" }, [
  /*#__PURE__*/_createElementVNode("div", {
    id: "oaiWidgetPreviewTitle",
    class: "truncate font-semibold text-gray-900 text-lg"
  }),
  /*#__PURE__*/_createElementVNode("div", { class: "bg-gray-300 flex-1 rounded" })
], -1)

import { PlusCircleIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { ChartBarIcon, CameraIcon, FolderIcon } from "@heroicons/vue/24/solid";
import { Component as VueComponent, ComputedRef } from "vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useHasPermission } from "shared/composables/project";
import { useTrackEvent } from "shared/composables/tracking";
import { useLogoUrl } from "@/composables/project";
import { useReports } from "@/composables/report";
import { DashboardGridSize, OculaiLayoutItem } from "@/types/Dashboard";
import { Report, ReportPlotType } from "@/types/Report";
import BaseWidget from "@/views/dashboard/componentsV2/BaseWidget.vue";
import {
  calculateRealWidgetHeight,
  calculateRealWidgetWidth,
} from "@/views/dashboard/services/projectOverviewV2Layout";
import AddReportMenu from "@/views/reports/components/AddReportMenu.vue";
import AddReportModal from "@/views/reports/components/AddReportModal.vue";
import reportIconsConfig from "@/views/reports/components/reportIconsConfig";

type Category = {
  name: string;
  widgets: OculaiLayoutItem[];
  icon: VueComponent;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'AddWidgetsSidebar',
  props: {
    widgets: {},
    dashboardGridSize: {}
  },
  emits: ["addWidget", "close", "widgetDragStart", "widgetDrag", "widgetDragEnd"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const addReportModalPlotType = ref<ReportPlotType | null>(null);
const widgetPreviewRef = ref<HTMLDivElement | null>(null);

const hasProcessesPermission = useHasPermission("processes_user");

// the logo is loaded only when the add widgets panel is opened,
// so it doesn't "block" loading of the entire dashboard
const { logoUrl } = useLogoUrl();

const trackEvent = useTrackEvent();

const { reports } = useReports();

const widgetsToUse = computed(() =>
  props.widgets.filter((widget) => widget.type !== "CustomerLogoWidget" || logoUrl.value),
);

const sortedReportWidgets = computed(() => {
  const reportWidgets = widgetsToUse.value.filter((widget) => widget.type === "ReportWidget");

  reportWidgets.sort((a, b) => a.name.localeCompare(b.name));

  const groupedReportWidget: Record<string, OculaiLayoutItem[]> = {};
  reportWidgets.forEach((widget) => {
    const type = getReportTypeById(widget?.arg ?? "");
    if (type) {
      if (!groupedReportWidget[type]) {
        groupedReportWidget[type] = [];
      }
      groupedReportWidget[type].push(widget);
    }
  });

  const entries = Object.entries(groupedReportWidget).sort((a, b) => a[0].localeCompare(b[0]));
  return entries.flatMap((entry) => entry[1]);
});

const categories = computed(() => {
  return [
    {
      name: "camera",
      widgets: widgetsToUse.value.filter((widget) => widget.type === "StreamWidget"),
      icon: CameraIcon,
    },
    {
      name: "report",
      widgets: sortedReportWidgets.value,
      icon: ChartBarIcon,
    },
    {
      name: "other",
      widgets: widgetsToUse.value.filter(
        (widget) => widget.type !== "StreamWidget" && widget.type !== "ReportWidget",
      ),
      icon: FolderIcon,
    },
  ].filter((category) => {
    if (category.name === "report") {
      return hasProcessesPermission;
    }
    return category.widgets.length > 0;
  });
}) as ComputedRef<Category[]>;

const handleReportDone = (report: Report) => {
  addReportModalPlotType.value = null;
  const widgetToAdd = props.widgets.find(
    (widget) => widget.type === "ReportWidget" && widget.arg === report._id,
  );
  if (widgetToAdd) {
    emit("addWidget", widgetToAdd);
  }
};

const prepareWidgetPreview = (widget: OculaiLayoutItem) => {
  if (!widgetPreviewRef.value) {
    return;
  }
  const width = calculateRealWidgetWidth(props.dashboardGridSize, widget.w);
  const height = calculateRealWidgetHeight(props.dashboardGridSize, widget.h);
  widgetPreviewRef.value.style.width = `${width}px`;
  widgetPreviewRef.value.style.height = `${height}px`;
  const titleElement = widgetPreviewRef.value.querySelector("#oaiWidgetPreviewTitle");
  if (titleElement) {
    titleElement.innerHTML = widget.name;
  }
};

const handleWidgetDragStart = (event: DragEvent, widget: OculaiLayoutItem) => {
  if (!event.dataTransfer) {
    return;
  }

  event.dataTransfer.effectAllowed = "move";

  event.dataTransfer.clearData();
  event.dataTransfer.setData("application/json", JSON.stringify(widget));

  if (widgetPreviewRef.value) {
    prepareWidgetPreview(widget);
    event.dataTransfer.setDragImage(widgetPreviewRef.value, 0, 0);
  }

  emit("widgetDragStart", event);
};
const getReportTypeById = (id: string) => {
  const report = reports.value.find((report) => report._id === id);
  return report?.type;
};
const getReportIcon = (id: string) => {
  const type = getReportTypeById(id);
  return type ? reportIconsConfig[type]?.icon : null;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (category) => {
          return (_openBlock(), _createElementBlock("div", {
            key: category.name,
            class: "flex flex-col gap-2"
          }, [
            _createElementVNode("div", {
              class: _normalizeClass([
              'font-semibold text-lg gap-2 items-center',
              (category.name !== 'report' || category.widgets.length) && 'flex justify-between',
            ])
            }, [
              _createElementVNode("div", _hoisted_4, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(category.icon), { class: "w-5 h-5 text-yellow-600" })),
                _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t(`dashboard.categories.${category.name}`)), 1),
                (category.name === 'report' && category?.widgets.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createVNode(AddReportMenu, {
                        onCreateReport: _cache[0] || (_cache[0] = ($event: any) => (addReportModalPlotType.value = $event)),
                        position: "top-4 -right-2",
                        width: "w-[280px]",
                        menuPosition: category.widgets.length ? 'left' : 'center',
                        class: "w-full"
                      }, {
                        icon: _withCtx(() => [
                          _createElementVNode("div", _hoisted_7, " + " + _toDisplayString(_unref(t)("analytics.reports.dashboard_new_report")), 1)
                        ]),
                        _: 2
                      }, 1032, ["menuPosition"])
                    ]))
                  : _createCommentVNode("", true)
              ])
            ], 2),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(category.widgets, (widget, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                class: "relative flex flex-col gap-2",
                key: `${widget?.type}_${widget?.arg}`
              }, [
                (
                category.name === 'report' &&
                widget?.arg &&
                getReportTypeById(widget?.arg) !==
                  getReportTypeById(category?.widgets[idx - 1]?.arg || '')
              )
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _hoisted_9,
                      _createElementVNode("span", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, [
                          (_openBlock(), _createBlock(_resolveDynamicComponent(getReportIcon(widget?.arg)), {
                            class: _normalizeClass(['stroke-gray-500 h-4 w-4 flex-shrink-0'])
                          })),
                          _createElementVNode("div", null, _toDisplayString(_unref(t)(`analytics.reports.${getReportTypeById(widget?.arg)}_plot`)), 1)
                        ])
                      ]),
                      _hoisted_12
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: "cursor-grab active:cursor-grabbing bg-gray-50 px-2 py-3 border text-sm rounded gap-1 select-none truncate",
                  onDblclick: ($event: any) => {
                emit('addWidget', widget);
                _unref(trackEvent)('dashboard_widget_add', { type: widget.type });
              },
                  draggable: "true",
                  onDragstart: ($event: any) => (handleWidgetDragStart($event, widget)),
                  onDrag: _cache[1] || (_cache[1] = ($event: any) => (emit('widgetDrag', $event))),
                  onDragend: ($event: any) => {
                emit('widgetDragEnd', $event);
                _unref(trackEvent)('dashboard_widget_add', { type: widget.type });
              }
                }, _toDisplayString(widget.name), 41, _hoisted_13)
              ]))
            }), 128)),
            (category?.widgets.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  (category.name === 'report')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        _createVNode(AddReportMenu, {
                          onCreateReport: _cache[2] || (_cache[2] = ($event: any) => (addReportModalPlotType.value = $event)),
                          position: "top-8 -left-[119px]",
                          width: "w-[280px]",
                          menuPosition: category.widgets.length ? 'left' : 'center',
                          class: "w-full"
                        }, {
                          icon: _withCtx(() => [
                            _createVNode(_unref(PlusCircleIcon), { class: "text-yellow-400 cursor-pointer hover:text-yellow-600 transition-colors w-10" })
                          ]),
                          _: 2
                        }, 1032, ["menuPosition"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ]))
        }), 128))
      ])
    ]),
    (widgetsToUse.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(_ctx.$t("dashboard.no_widgets_to_add")), 1))
      : _createCommentVNode("", true),
    _createVNode(_unref(XMarkIcon), {
      class: "h-5 w-5 absolute cursor-pointer top-3 right-4 text-gray-600",
      onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('close')))
    }),
    (addReportModalPlotType.value)
      ? (_openBlock(), _createBlock(AddReportModal, {
          key: 1,
          plotType: addReportModalPlotType.value,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (addReportModalPlotType.value = null)),
          onDone: handleReportDone
        }, null, 8, ["plotType"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      ref_key: "widgetPreviewRef",
      ref: widgetPreviewRef,
      class: "fixed",
      style: {
        top: '-10000px',
        left: '-10000px',
      }
    }, [
      _createVNode(BaseWidget, { class: "px-4 py-3" }, {
        default: _withCtx(() => [
          _hoisted_17
        ]),
        _: 1
      })
    ], 512)
  ]))
}
}

})